import { useRef, useEffect, useState, RefObject } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

interface FadeInProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  from?: 'bottom' | 'left' | 'right' | 'top';
  distance?: number;
  duration?: number;
}

export function FadeIn({
  children,
  delay = 0,
  className = '',
  from = 'bottom',
  distance = 28,
  duration = 0.75,
}: FadeInProps) {
  const { ref, isVisible } = useScrollAnimation();
  const translateMap: Record<string, string> = {
    bottom: `translateY(${distance}px)`,
    top: `translateY(-${distance}px)`,
    left: `translateX(-${distance}px)`,
    right: `translateX(${distance}px)`,
  };
  return (
    <div
      ref={ref as RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0,0)' : translateMap[from],
        transition: `opacity ${duration}s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform ${duration}s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

interface ScaleInProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

export function ScaleIn({ children, delay = 0, className = '' }: ScaleInProps) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.94)',
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

interface LineRevealProps {
  className?: string;
  delay?: number;
  color?: string;
}

export function LineReveal({ className = '', delay = 0, color = 'bg-divider' }: LineRevealProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });
  return (
    <span
      ref={ref as RefObject<HTMLSpanElement>}
      className={`block h-px ${color} ${className}`}
      style={{
        transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
        transformOrigin: 'left center',
        transition: `transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    />
  );
}

interface StaggerContainerProps<T> {
  items: T[];
  renderItem: (item: T, idx: number, isVisible: boolean) => React.ReactNode;
  className?: string;
  intervalMs?: number;
}

export function StaggerContainer<T>({
  items,
  renderItem,
  className = '',
  intervalMs = 90,
}: StaggerContainerProps<T>) {
  const { ref, visibleCount } = useStaggerAnimation(items.length, { threshold: 0.08 });
  return (
    <div ref={ref as RefObject<HTMLDivElement>} className={className}>
      {items.map((item, idx) => renderItem(item, idx, visibleCount > idx))}
    </div>
  );
}

interface CountUpProps {
  target: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  className?: string;
}

export function CountUp({ target, suffix = '', prefix = '', duration = 1800, className = '' }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
}
